import React, { useState } from "react";
import { cn } from "../../../lib/utils";
import { Copy, Check, ChevronDown, ChevronRight } from "lucide-react";
import { copyToClipboard, prettyJson } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";

interface GatewayJsonViewerProps {
  data: any;
  label?: string;
  collapsed?: boolean;
  maxHeight?: string;
  className?: string;
  unwrap?: boolean;
}

function normalizePayload(raw: any, maxDepth = 6): any {
  let current = raw;
  for (let i = 0; i < maxDepth; i++) {
    if (current && typeof current === "object") {
      if (Array.isArray(current.content) && current.content.length > 0 && current.content[0]?.text) {
        current = current.content[0].text;
        continue;
      }
      if (Array.isArray(current) && current.length > 0 && current[0]?.text) {
        current = current[0].text;
        continue;
      }
      if (typeof current.text === "string") {
        current = current.text;
        continue;
      }
    }
    if (typeof current === "string") {
      try {
        current = JSON.parse(current);
        continue;
      } catch {
        return current;
      }
    }
    break;
  }
  return current;
}

export default function GatewayJsonViewer({ data, label, collapsed = false, maxHeight = "400px", className, unwrap = false }: GatewayJsonViewerProps) {
  const { toast } = useToast();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [copied, setCopied] = useState(false);
  const normalized = unwrap ? normalizePayload(data) : data;
  const jsonStr = prettyJson(normalized);

  const handleCopy = async () => {
    const ok = await copyToClipboard(jsonStr);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Copied to clipboard" });
    }
  };

  if (!data) return null;

  return (
    <div className={cn("rounded-lg border border-border bg-muted/70 overflow-hidden", className)}>
      {label && (
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground/80 bg-card"
        >
          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {label}
        </button>
      )}
      {!isCollapsed && (
        <div className="relative">
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 p-1.5 rounded-md bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground/80 transition-colors z-10"
            title="Copy"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <pre
            className="p-3 text-xs font-mono overflow-auto text-foreground/80 leading-relaxed"
            style={{ maxHeight }}
          >
            <JsonHighlight json={jsonStr} />
          </pre>
        </div>
      )}
    </div>
  );
}

function JsonHighlight({ json }: { json: string }) {
  const lines = json.split("\n");
  return (
    <>
      {lines.map((line, i) => (
        <div key={i}>
          {highlightLine(line)}
        </div>
      ))}
    </>
  );
}

function highlightLine(line: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = line;
  let key = 0;

  const keyMatch = remaining.match(/^(\s*)"([^"]+)"(\s*:\s*)/);
  if (keyMatch) {
    parts.push(<span key={key++}>{keyMatch[1]}</span>);
    parts.push(<span key={key++} className="text-primary">"{keyMatch[2]}"</span>);
    parts.push(<span key={key++}>{keyMatch[3]}</span>);
    remaining = remaining.slice(keyMatch[0].length);
  }

  // Match value types
  if (remaining.match(/^"([^"]*)"/)) {
    const m = remaining.match(/^"([^"]*)"(.*)/);
    if (m) {
      parts.push(<span key={key++} className="text-blue-400">"{m[1]}"</span>);
      parts.push(<span key={key++}>{m[2]}</span>);
      return <>{parts}</>;
    }
  }
  if (remaining.match(/^-?\d+\.?\d*/)) {
    const m = remaining.match(/^(-?\d+\.?\d*)(.*)/);
    if (m) {
      parts.push(<span key={key++} className="text-purple-400">{m[1]}</span>);
      parts.push(<span key={key++}>{m[2]}</span>);
      return <>{parts}</>;
    }
  }
  if (remaining.match(/^(true|false)/)) {
    const m = remaining.match(/^(true|false)(.*)/);
    if (m) {
      parts.push(<span key={key++} className="text-rose-400">{m[1]}</span>);
      parts.push(<span key={key++}>{m[2]}</span>);
      return <>{parts}</>;
    }
  }
  if (remaining.match(/^null/)) {
    const m = remaining.match(/^(null)(.*)/);
    if (m) {
      parts.push(<span key={key++} className="text-muted-foreground">{m[1]}</span>);
      parts.push(<span key={key++}>{m[2]}</span>);
      return <>{parts}</>;
    }
  }

  parts.push(<span key={key++}>{remaining}</span>);
  return <>{parts}</>;
}
